import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import { newToastEvent, updateToastEvent } from "../../../utils/ToastUtil";
import { AppConfirm } from "../../../utils/PopupUtil";
import useCloudPermissions from "../../../hooks/useCloudPermissions";
import TenantAPI from "../../api/TenantAPI";
import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";

const ConfirmBulkDelete = async () => {
  const result = await AppConfirm({
    title: "Delete Records?",
    text: "Are you sure you want to delete these records?",
  });

  return result.isConfirmed;
};

/**
 * useBulkDeleteRecords
 *
 * This hook is used to delete multiple records at once from the cloud.
 * Optionally supports optimistic updates.
 *
 * @param {bool=} useOptimisticUpdate - Whether to use optimistic updates or not.
 */
const useBulkDeleteRecords = (useOptimisticUpdate = false) => {
  const { cPermissions } = useCloudPermissions();
  const queryClient = useQueryClient();
  const tenantID = useAtomValue(tenantIDAtom);

  const updateRecordsQueries = (moduleAPIName: string, recordIDs: string[]) => {
    // For every records query for this module, remove the deleted records.
    queryClient.setQueriesData(
      { queryKey: [tenantID, "records", moduleAPIName] },
      (prev: any) => {
        if (!prev?.records) return;

        const newRecords = prev.records.filter(
          (r: any) => !recordIDs.includes(r.id)
        );

        return {
          ...prev,
          records: newRecords,
        };
      }
    );
  };

  const bulkDeleteRecords = useMutation({
    mutationFn: ({
      moduleAPIName,
      payload,
    }: {
      moduleAPIName: string;
      payload: any;
      onSuccess?: () => void;
    }) => {
      if (!cPermissions.cloud_delete_record) {
        return Promise.reject(
          "You do not have permission to delete records in this cloud."
        );
      }

      return TenantAPI.bulkDeleteRecords(moduleAPIName, payload);
    },
    onSuccess: (_, { onSuccess }) => {
      if (useOptimisticUpdate) return; // Non optimistic updates are handled by onSuccess.

      callFunctionIfExists(onSuccess);

      newToastEvent("success", "Deleting Records...", "bulk-deletion", {
        loading: true,
        autoClose: false,
      });
    },
    onError: (err) => {
      newToastEvent("error", getErrorMessage(err, "Failed to delete records."));
    },
  });

  return {
    confirmBulkDelete: ConfirmBulkDelete,
    bulkDeleteRecords: bulkDeleteRecords.mutate,
    deletingRecords: bulkDeleteRecords.isPending,
    deleteRecordsError: bulkDeleteRecords.error
      ? getErrorMessage(bulkDeleteRecords.error)
      : null,
  };
};

export default useBulkDeleteRecords;
